import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

/**
 * The automated purchase check, as the person in front of it is allowed to
 * see it.
 *
 * Three rules decide whether a payment goes straight to Accounts or needs
 * management sign-off: value under the ceiling, not already in stock, and
 * margin over the minimum for that container type.
 *
 * THE MONEY IS NOT HIDDEN HERE — it is never sent here. The server strips the
 * figures for anyone outside Accounts and management, so this component
 * renders whatever it was given and cannot leak what it does not have. What
 * Sales and Purchasing get instead is the action hint: "procure at a lower
 * rate" tells them what to do about a thin margin without telling them the
 * margin, which is the whole point of the split.
 */

export interface Criterion {
  key: 'value' | 'stock' | 'margin';
  label: string;
  status: 'pass' | 'fail' | 'unknown' | 'na';
  statement: string;
  hint: string;
}

export interface CriteriaVerdict {
  indentId: string;
  passed: boolean;
  requiresOverride: boolean;
  criteria: Criterion[];
  failedKeys: string[];
  containerClass: { sizeFt: number | null; build: 'GP' | 'HC' | null; label: string };
  ruleApplied: { label: string; minMarginAmount: number; minMarginPct: number };
  summary: string;
  disabled?: boolean;
  /** Present only for Accounts and management. */
  confidential?: {
    purchaseCost: number;
    purchaseCostBasis: string;
    saleValue: number;
    saleSource: string;
    saleOrderNumber: string;
    quantity: number;
    marginTotal: number | null;
    marginPerUnit: number | null;
    marginPct: number | null;
    minMarginAmount: number;
    minMarginPct: number;
    shortfallPerUnit: number | null;
  };
}

const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;

/** Live verdict for one indent. `amount` re-runs the value check against the
 *  tranche actually being requested rather than the indent total. */
export function usePurchaseCriteria(indentId: string, amount?: number, enabled = true) {
  const amt = Number(amount) > 0 ? Math.round(Number(amount)) : 0;
  return useQuery<{ criteria: CriteriaVerdict; canSeeFinancials: boolean }>({
    queryKey: ['procurement', 'criteria', indentId, amt],
    queryFn: () => api.get(
      `/api/procurement/purchase-criteria/indent/${encodeURIComponent(indentId)}${amt ? `?amount=${amt}` : ''}`,
    ),
    enabled: enabled && !!indentId,
    // The underlying reads hit Google Sheets, which rate-limits hard. A short
    // cache keeps a user nudging the amount field from spending the quota.
    staleTime: 60_000,
    retry: false,
  });
}

function Mark({ status }: { status: Criterion['status'] }) {
  const map = {
    pass: { text: 'PASS', cls: 'bg-emerald-100 text-emerald-700' },
    fail: { text: 'FAIL', cls: 'bg-rose-100 text-rose-700' },
    unknown: { text: 'NOT CHECKED', cls: 'bg-amber-100 text-amber-800' },
    na: { text: 'N/A', cls: 'bg-slate-100 text-slate-500' },
  }[status];
  return (
    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold tracking-wide ${map.cls}`}>
      {map.text}
    </span>
  );
}

export default function PurchaseCriteriaCard({
  verdict,
  canSeeFinancials,
  loading,
  compact,
}: {
  verdict?: CriteriaVerdict | null;
  canSeeFinancials?: boolean;
  loading?: boolean;
  compact?: boolean;
}) {
  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-3 text-[11px] text-slate-400">
        Running the automated checks…
      </div>
    );
  }
  if (!verdict) return null;

  if (verdict.disabled) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-500">
        Automated purchase checks are switched off — this will go to Accounts as before.
      </div>
    );
  }

  const bad = verdict.requiresOverride;
  const conf = verdict.confidential;

  return (
    <div
      className={`rounded-lg border p-3 ${bad
        ? 'border-amber-300 bg-amber-50'
        : 'border-emerald-300 bg-emerald-50'}`}
    >
      <div className="flex items-start gap-2">
        <span
          className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold tracking-wide ${bad ? 'bg-amber-200 text-amber-900' : 'bg-emerald-200 text-emerald-900'
            }`}
        >
          {bad ? 'NEEDS SIGN-OFF' : 'CLEARED'}
        </span>
        <div className="min-w-0 flex-1">
          <p className={`text-[11.5px] font-bold ${bad ? 'text-amber-900' : 'text-emerald-900'}`}>
            {bad
              ? 'This does not clear automatically'
              : 'All automated checks cleared — straight to Accounts'}
          </p>
          <p className="text-[10.5px] text-slate-600">
            Checked as <b>{verdict.containerClass.label}</b>
            {verdict.ruleApplied?.label ? ` · ${verdict.ruleApplied.label} rule` : ''}
          </p>
        </div>
      </div>

      <ul className="mt-2 space-y-1.5">
        {verdict.criteria.map((c) => (
          <li key={c.key} className="flex items-start gap-2">
            <Mark status={c.status} />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold text-slate-700">{c.label}</p>
              <p className="text-[10.5px] text-slate-600">{c.statement}</p>
              {/*
                The action hint. This is what a purchaser acts on when the
                margin failed — deliberately phrased as an instruction, since
                they are not shown the number behind it.
              */}
              {c.status !== 'pass' && c.hint && (
                <p className="mt-0.5 text-[10.5px] font-medium text-slate-800">→ {c.hint}</p>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/*
        Figures, for the two roles allowed them. Absent from the payload
        entirely for everyone else — this block simply has nothing to render.
      */}
      {canSeeFinancials && conf && !compact && (
        <div className="mt-2.5 border-t border-slate-300/60 pt-2">
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
            Restricted · Accounts and management only
          </p>
          <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[10.5px]">
            <div>
              <dt className="text-slate-500">Purchase cost</dt>
              <dd className="font-mono font-bold text-slate-800">
                {inr(conf.purchaseCost)}
                <span className="ml-1 font-sans font-normal text-slate-400">({conf.purchaseCostBasis})</span>
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Sale value</dt>
              <dd className="font-mono font-bold text-slate-800">
                {conf.saleValue > 0 ? inr(conf.saleValue) : '—'}
                {conf.saleOrderNumber && (
                  <span className="ml-1 font-sans font-normal text-slate-400">{conf.saleOrderNumber}</span>
                )}
              </dd>
            </div>
            {conf.marginTotal != null && (
              <>
                <div>
                  <dt className="text-slate-500">Gross margin</dt>
                  <dd className="font-mono font-bold text-slate-800">
                    {inr(conf.marginTotal)}
                    {conf.marginPct != null && (
                      <span className="ml-1 font-sans font-normal text-slate-400">{conf.marginPct}%</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Per container</dt>
                  <dd
                    className={`font-mono font-bold ${conf.shortfallPerUnit ? 'text-rose-700' : 'text-emerald-700'}`}
                  >
                    {conf.marginPerUnit != null ? inr(conf.marginPerUnit) : '—'}
                    <span className="ml-1 font-sans font-normal text-slate-400">
                      vs {inr(conf.minMarginAmount)} min
                    </span>
                  </dd>
                </div>
              </>
            )}
          </dl>
          {/*
            Where the sale came from. A stock purchase has no client order of
            its own, so its revenue is found through the containers that went
            out against one — worth saying, because that figure is a lookup
            rather than something recorded on this indent.
          */}
          {conf.saleSource === 'container-map' && (
            <p className="mt-1 text-[10px] text-slate-500">
              Sale matched through the containers that went out against {conf.saleOrderNumber}.
            </p>
          )}
          {conf.saleSource === 'unreadable' && (
            <p className="mt-1 text-[10px] text-amber-700">
              The sales sheet could not be read just now — this is not a statement that no sale exists.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
